import React from "react";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip, Legend } from "recharts";
import EmptyState from "./EmptyState";
import TooltipUI from "./Tooltip";
import InfoIcon from "./InfoIcon";

const ToneDistributionChart = ({ data }) => {
  // Check if we have actual data
  const hasData = data && data.length > 0 && data.some(item => item.value > 0);
  
  if (!hasData) {
    return (
      <div className="bg-white-f p-5 rounded-lg border border-grey-20 shadow-subtle">
        <div className="flex items-center gap-1 mb-4">
          <h3 className="text-sm font-normal text-black-0">Tone distribution</h3>
          <TooltipUI
            content="Breakdown of communication tones inferred from messages (e.g., collaborative, technical, urgent) over the selected period."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>
        <EmptyState
          variant="no-data"
          title="No tone data available"
          description="There's no tone analysis data for the selected period. Try selecting a different date range."
          actionButton={null}
        />
      </div>
    );
  }

  // Default data structure for ensuring all expected tones are present
  const defaultData = [
    { name: "Urgent",        value: 0, messageCount: 0, color: "#FF5252" },
    { name: "Assertive",     value: 0, messageCount: 0, color: "#FF9100" },
    { name: "Formal",        value: 0, messageCount: 0, color: "#C6FF00" },
    { name: "Informal",      value: 0, messageCount: 0, color: "#00E676" },
    { name: "Technical",     value: 0, messageCount: 0, color: "#00E5FF" },
    { name: "Collaborative", value: 0, messageCount: 0, color: "#448AFF" },
    { name: "Creative",      value: 0, messageCount: 0, color: "#7C4DFF" },
    { name: "Casual",        value: 0, messageCount: 0, color: "#FF4081" },
  ];

  // Ensure all expected tones are present
  const completeData = defaultData.map(defaultTone => {
    const existingTone = data.find(tone => tone.name === defaultTone.name);
    return existingTone || defaultTone;
  });

  // Calculate total percentage
  const total = completeData.reduce((sum, item) => sum + (item.value || 0), 0);

  // Calculate most frequent tone - only from tones with actual values
  const getMostFrequentTone = () => {
    const tonesWithValues = completeData.filter(item => (item.value || 0) > 0);
    
    if (!tonesWithValues || tonesWithValues.length === 0 || total === 0) {
      return { name: "Balanced", color: "#ABABBA" };
    }
    
    const maxItem = tonesWithValues.reduce((max, current) => 
      (current.value || 0) > (max.value || 0) ? current : max
    );
    
    return maxItem;
  };

  const mostFrequentTone = getMostFrequentTone();

  // Transform data for radar chart (rescale for better visual representation)
  const radarData = completeData.map(item => {
    const value = item.value || 0;
    const percentage = total > 0 ? (value / total) * 100 : 0;
    // Apply square root scaling to make small values more visible, then scale to 0-60 range
    const scaledValue = percentage > 0 ? Math.sqrt(percentage) * 8 : 0;
    return {
      ...item,
      value: value, // Keep original value
      normalizedValue: Math.min(scaledValue, 60), // Cap at 60 for good visual proportion
      actualPercentage: Math.round(percentage) // Keep original percentage for tooltip
    };
  });

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      
      return (
        <div className="bg-white-f p-3 border border-grey-20 rounded-md shadow-subtle">
          <p className="text-sm font-medium" style={{ color: item.color }}>{item.name}</p>
          <p className="text-sm text-grey-60">
            <span className="font-medium">{item.actualPercentage}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  console.log('ToneDistributionChart data:', completeData);
  console.log('Most frequent tone:', mostFrequentTone);
  console.log('Total:', total);

  return (
    <div className="bg-white-f p-5 rounded-lg border border-grey-20 shadow-subtle h-full flex flex-col">
      <div className="mb-4 flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-normal text-black-0">Tone distribution</h3>
          <TooltipUI
            content="Breakdown of communication tones inferred from messages (e.g., collaborative, technical, urgent) over the selected period."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>
        <div className="flex items-baseline gap-2">
          <p className="font-medium text-[18px] text-black-0">
            {mostFrequentTone.name}
          </p>
          <span className="text-[12px] text-grey-60">is dominant tone</span>
        </div>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="w-full h-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <PolarGrid stroke="#F0F0F5" />
              <PolarAngleAxis 
                dataKey="name" 
                tick={{ fontSize: 11, fill: '#ABABBA' }}
                className="text-xs"
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 60]}
                tick={false}
                axisLine={false}
              />
              <Radar
                name="Tone Distribution"
                dataKey="normalizedValue"
                stroke="#4747EB"
                fill="#4747EB"
                fillOpacity={0.2}
                strokeWidth={2}
                dot={false}
              />
              <Tooltip content={<CustomTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ToneDistributionChart; 